<template>
    <dashboard class="student-courses">
        <row>
            <panel>
                <h3 slot="title">Courses</h3>
                <template slot="buttons">
                    <form class="mobile-hidden week-date-picker">
                        <app-button
                            icon="icon-paginateleft"
                            type="clear"
                            size="small"
                            prepend
                            @click="previousWeek">
                            <span class="visuallyhidden">Previous Week</span>
                        </app-button>
                        <label title="Week Ending In" for="week-picker">
                            <span class="visuallyhidden">Week Ending In</span>
                            <app-date-picker
                                id="week-picker"
                                v-model="week"
                                size="small"
                                placeholder="Pick a week"/>
                        </label>
                        <app-button
                            icon="icon-paginateright"
                            type="clear"
                            size="small"
                            append
                            @click="nextWeek">
                            <span class="visuallyhidden">Next Week</span>
                        </app-button>
                    </form>
                    <app-button size="small" @click="showAddCourse = true">Add Course</app-button>
                </template>

                <course-table
                    :items="courses"
                    :columns="['title','time','location','attendance']"
                    :allow-sort="true"
                    :remote-sort="false"
                    :sort_by="'start_time'"
                    :student="student">
                    <template slot="actions">
                        <el-table-column label="Actions" width="100px" align="right">
                            <template slot-scope="scope">
                                <div class="table-buttons">
                                    <app-button
                                        size="mini"
                                        icon="icon-notes"
                                        @click.stop="handleNotes(scope.$index, scope.row)">
                                        <span class="visuallyhidden">Comments</span>
                                    </app-button>
                                    <app-tooltip effect="dark" content="Remove From Course" placement="top-end">
                                        <app-button
                                            type="danger"
                                            size="mini"
                                            icon="icon-delete"
                                            @click.stop="handleRemove(scope.$index, scope.row)">
                                            <span class="visuallyhidden">Remove Student From Course</span>
                                        </app-button>
                                    </app-tooltip>
                                </div>
                            </template>
                        </el-table-column>
                    </template>
                </course-table>
            </panel>
        </row>

        <modal v-model="showAddCourse">
            <student-add-course :student="student"/>
        </modal>
        <modal v-model="showNotes">
            <notes-view :student-uuid="student.uuid" :class-uuid="selectedClass.uuid"/>
        </modal>
    </dashboard>
</template>

<script>
import { mapActions } from 'vuex'
import { get } from '@/utilities'
import moment from 'moment'

import StudentAddCourse from './StudentAddCourse'
import NotesView from './Notes/NotesView'
import CourseTable from '../Course/CourseTable'

import Student from '@/store/models/Student'
import Course from '@/store/models/Course'

export default {
    name: 'page-students-detail-courses',
    components: {
        CourseTable,
        StudentAddCourse,
        NotesView
    },
    props: ['uuid', 'student'],
    data() {
        return {
            courseIds: [],
            showAddCourse: false,
            showNotes: false,
            selectedClass: {},
        }
    },
    computed: {
        week: {
            get() {
                return this.$store.state.params.week
            },
            set(value) {
                this.updateWeek(value)
            }
        },
        courses() {
            let weekStart = moment(this.week).utc().startOf('week')
            let weekEnd = moment(this.week).utc().endOf('week')
            return Course.query()
                .with('location')
                .with('attendance', (query) => {
                    query.where('student_id', this.student.id)
                        .where((att) => {
                            return moment(att.created_at).isBetween(
                                weekStart,
                                weekEnd,
                                null,
                                '[)'
                            )
                        })
                        .orderBy('created_at')
                })
                .with('attendance.coordinates')
                .whereIdIn(this.courseIds)
                .get()
        }
    },
    mounted() {
        this.courseIds = this.student.courses.map(item => item.id)
    },
    methods: {
        ...mapActions([
            'updateWeek'
        ]),
        navigateToClassDetail(course) {
            this.$router.push({
                name: 'classes.detail',
                params: { uuid: course.uuid }
            })
        },
        handleNotes(index, row) {
            this.selectedClass = row
            this.showNotes = true
        },
        handleRemove(index, row) {
            this.$confirm({
                title: 'Confirm',
                message: 'Are you sure you want to remove this class?'
            })
                .then(() => {
                    return this.student.$detach(row.id, 'courses', 'removeStudentFromCourse')
                })
                .then(() => {
                    this.$notify({
                        title: 'Success',
                        message: 'The class has been removed from the student!',
                        type: 'success',
                        offset: 75,
                        duration: 2000
                    })
                })
        },
        previousWeek() {
            this.updateWeek(moment(this.week).subtract(7, 'd').toDate())
        },
        nextWeek() {
            this.updateWeek(moment(this.week).add(7, 'd').toDate())
        },
        get
    },
    watch: {
        week(week, oldWeek) {
            if (week.toDateString() !== oldWeek.toDateString()) {
                Student.$one({ uuid: this.uuid, week }).then(({ raw }) => {
                    this.courseIds = raw.courses.map(item => item.id)
                })
            }
        },
        student(student) {
            this.courseIds = student.courses.map(item => item.id)
        }
    }
}
</script>

<style lang="stylus">
.student-courses
    .week-date-picker
        .el-date-editor
            width: 175px
</style>
